import React, { useEffect, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import "./PlumaAnalytics.css";

const INSIGHTS_URL =
  window.location.hostname === "localhost"
    ? "http://127.0.0.1:5001/gofoton-55796/us-central1/analyticsInsights"
    : "https://us-central1-gofoton-55796.cloudfunctions.net/analyticsInsights";

async function getAuthHeaders() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Administrator sign-in is required.");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function AnalyticsInsightsChat({ activeRange }) {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [usage, setUsage] = useState({
    requests: 0,
    trackedRequests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    cachedInputTokens: 0,
    reasoningTokens: 0,
  });
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");

  const bottomRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setLoadingHistory(true);
      setError("");

      try {
        const headers = await getAuthHeaders();

        const response = await fetch(INSIGHTS_URL, {
          method: "GET",
          headers,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `Insights history failed (${response.status}).`);
        }

        if (!cancelled) {
          setMessages(Array.isArray(data.messages) ? data.messages : []);

          setUsage({
            requests: Number(data.usage?.requests || 0),

            trackedRequests: Number(data.usage?.trackedRequests || 0),

            inputTokens: Number(data.usage?.inputTokens || 0),

            outputTokens: Number(data.usage?.outputTokens || 0),

            totalTokens: Number(data.usage?.totalTokens || 0),

            cachedInputTokens: Number(data.usage?.cachedInputTokens || 0),

            reasoningTokens: Number(data.usage?.reasoningTokens || 0),
          });
        }
      } catch (err) {
        console.error("Analytics Insights history error:", err);

        if (!cancelled) {
          setError(err.message || "Could not load Insights history.");
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, asking]);

  const askQuestion = async () => {
    const cleanQuestion = question.trim();

    if (!cleanQuestion || asking) return;

    setQuestion("");
    setError("");
    setAsking(true);

    const optimisticMessage = {
      id: `local-${Date.now()}`,
      question: cleanQuestion,
      answer: "",
      rangeKey: activeRange,
      adminName: "You",
      createdAt: new Date().toISOString(),
      pending: true,
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(INSIGHTS_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          question: cleanQuestion,
          rangeKey: activeRange,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Insights request failed (${response.status}).`);
      }

      setMessages((prev) =>
        prev.map((message) =>
          message.id === optimisticMessage.id
            ? {
                ...data,
                pending: false,
              }
            : message
        )
      );

      setUsage((prev) => ({
        requests: prev.requests + 1,

        trackedRequests: prev.trackedRequests + (Number(data.totalTokens || 0) > 0 ? 1 : 0),

        inputTokens: prev.inputTokens + Number(data.inputTokens || 0),

        outputTokens: prev.outputTokens + Number(data.outputTokens || 0),

        totalTokens: prev.totalTokens + Number(data.totalTokens || 0),

        cachedInputTokens: prev.cachedInputTokens + Number(data.cachedInputTokens || 0),

        reasoningTokens: prev.reasoningTokens + Number(data.reasoningTokens || 0),
      }));
    } catch (err) {
      console.error("Analytics Insights request error:", err);

      setMessages((prev) => prev.filter((message) => message.id !== optimisticMessage.id));

      setQuestion(cleanQuestion);

      setError(err.message || "Analytics Insights could not answer that question.");
    } finally {
      setAsking(false);
    }
  };

  const starterQuestions = [
    "What changed most in this period?",
    "What products are getting the most interest?",
    "What are customers asking Pluma about?",
    "Which pages look weak or underperforming?",
    "What should we improve on the website?",
    "What are the biggest opportunities in this data?",
  ];

  return (
    <div className="analytics-insights-root">
      <div className="analytics-insights-header">
        <div>
          <h2>Analytics Insights</h2>
          <p>
            Ask questions about Go!Foton analytics. The selected range is emphasized, while Insights can also use all
            available historical analytics when the question calls for it.
          </p>
        </div>

        <div className="analytics-insights-range">{activeRange || "last90"}</div>
      </div>

      {error && <div className="analytics-insights-error">{error}</div>}

      {loadingHistory ? (
        <div className="analytics-insights-loading">Loading shared Insights history...</div>
      ) : (
        <>
          {messages.length === 0 && (
            <div className="analytics-insights-empty-state">
              <h3>Ask Go!Foton's analytics</h3>
              <p>
                Insights can compare periods, identify product interest, examine Pluma behavior, review traffic sources,
                and surface website opportunities.
              </p>

              <div className="analytics-insights-starters">
                {starterQuestions.map((starter) => (
                  <button key={starter} type="button" onClick={() => setQuestion(starter)}>
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="analytics-insights-history">
            {messages.map((message) => (
              <div key={message.id} className={`analytics-insights-message ${message.pending ? "is-pending" : ""}`}>
                <div className="analytics-insights-question">
                  <div className="analytics-insights-meta">
                    <strong>{message.adminName || "Admin"}</strong>

                    <span>{formatDate(message.createdAt)}</span>

                    {message.rangeKey && <span>{message.rangeKey}</span>}
                  </div>

                  <p>{message.question}</p>
                </div>

                <div className="analytics-insights-answer">
                  <div className="analytics-insights-ai-label">Insights</div>

                  {message.pending ? (
                    <div className="analytics-insights-thinking">
                      <span />
                      <span />
                      <span />
                      Analyzing website data...
                    </div>
                  ) : (
                    <p>{message.answer}</p>
                  )}
                </div>
              </div>
            ))}

            {asking && messages.every((message) => !message.pending) && (
              <div className="analytics-insights-thinking standalone">
                <span />
                <span />
                <span />
                Analyzing website data...
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </>
      )}

      <form
        className="analytics-insights-input-row"
        onSubmit={(event) => {
          event.preventDefault();
          askQuestion();
        }}
      >
        <textarea
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              askQuestion();
            }
          }}
          placeholder="Ask about visitors, products, Pluma, pages, traffic sources, conversions, search behavior..."
          disabled={asking}
          rows={3}
        />

        <button type="submit" disabled={asking || !question.trim()}>
          {asking ? "Analyzing..." : "Ask Insights"}
        </button>
      </form>

      <div className="analytics-insights-footnote">
        Uses aggregate Go!Foton analytics and Pluma activity from the selected period plus available historical data.
        Results should be treated as analysis, not proof of causation.
      </div>
    </div>
  );
}

export default AnalyticsInsightsChat;
